import React, { useMemo } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { FormHelperText, Box } from "@mui/material";
import dayjs from "dayjs";

interface BasicDatePickerProps {
  label: string;
  width?: string;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  value: dayjs.Dayjs | null;
  onChange: (date: Date | null) => void;
  onError?: (reason: string | null) => void;
  helperText?: string;
  error?: boolean;
  padding?: string;
  onBlur?: () => void;
}

export default function BasicDatePicker({
  label,
  width = "100%",
  padding = "10px 4px",
  minDate,
  maxDate,
  disabled = false,
  required = false,
  readOnly = false,
  value,
  onChange,
  onError,
  helperText,
  error = false,
  onBlur
}: BasicDatePickerProps) {
  const hasLabel = Boolean(label?.trim());
  const normalizedValue = useMemo(() => {
    return value ? dayjs(value) : null;
  }, [value]);

  // Build text field styles, removing hover border color when disabled
  const textFieldSx = useMemo(() => {
    const base: any = {
      minWidth: "100% !important",
      width: width,
      "& .MuiOutlinedInput-root": {
        height: "36px",
        borderRadius: "6px",
        fontSize: "11px",
        fontWeight: "400",
        fontFamily: "Jost",
        paddingRight: "0px",
        "& input": {
          height: "36px",
          fontSize: "11px",
          color: "#a5a3ae",
          boxSizing: "border-box",
          background: "transparent"
        },
        "& .MuiInputAdornment-root": {
          marginRight: "0px !important",
        },
        "& .MuiInputAdornment-root .MuiIconButton-edgeEnd": {
          marginRight: "0px !important",
          padding: "4px",
        },
        "& fieldset": {
          borderColor: error ? "#d32f2f" : "default",
        },
      },
    };

    if (!disabled) {
      base["& .MuiOutlinedInput-root"]["&:hover fieldset"] = {
        borderColor: error ? "#d32f2f" : "#1CB1B1",
      };
    }

    // Keep focused state styling as-is; disabled inputs cannot be focused
    base["& .MuiOutlinedInput-root"]["&.Mui-focused fieldset"] = {
      borderColor: error ? "#d32f2f" : "#1CB1B1",
    };

    return base;
  }, [width, disabled, error]);

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date === null) {
      onChange(null);
    } else {
      onChange(date.toDate());
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="form-container">
        {hasLabel && (
          <label className="text-lable">
            {label}
            {required && <span style={{ color: "red" }}> *</span>}
          </label>
        )}

        <Box sx={{ width: "100%" }}>
         <DatePicker
          value={normalizedValue}
          minDate={minDate || dayjs("1900-01-01")}
          maxDate={maxDate || dayjs("9999-12-31")}
          disabled={disabled}
          onChange={handleDateChange}
            onError={(reason) => {
              onError?.((reason as string | null) ?? null);
            }}
          className="date-picker"
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              error: error,
              fullWidth: true,
              sx: textFieldSx,
              onBlur: () => {onBlur},
              inputProps: { readOnly: readOnly },
            },
            actionBar: {
              sx: {
                "& .MuiButton-root": {
                  color: "#1CB1B1 !important",
                },
              },
            },
            layout: {
              sx: {
                "& .MuiPickersDay-root.Mui-selected": {
                  backgroundColor: "#1CB1B1 !important",
                },
                "& .MuiPickersDay-root.Mui-selected:hover": {
                  backgroundColor: "#169a9a !important",
                },
                "& .MuiPickersDay-today": {
                  borderColor: "#1CB1B1 !important",
                },
                "& .MuiPickersYear-yearButton.Mui-selected": {
                  backgroundColor: "#1CB1B1 !important",
                },
              },
            },
            popper: {
              disablePortal: false,
              modifiers: [
                {
                  name: "flip",
                  enabled: true,
                  options: {
                    rootBoundary: "viewport",
                    fallbackPlacements: ["top", "bottom"],
                    padding: 8,
                  },
                },
                {
                  name: "preventOverflow",
                  enabled: true,
                  options: {
                    rootBoundary: "viewport",
                    tether: false,
                    altAxis: true,
                  },
                },
                {
                  name: "shift",
                  enabled: true,
                },
              ],
              sx: {
                zIndex: 99999,
                "& .MuiPaper-root": {
                  boxShadow: "0px 10px 15px -3px rgba(0,0,0,0.1)",
                  borderRadius: "8px",
                  "@media (max-height: 700px)": {
                    transform: "scale(0.9)",
                    transformOrigin: "top",
                  },
                },
              },
            },
          }}
        />
        </Box>

        {helperText && (
          <FormHelperText error={error} sx={{ marginLeft: "4px" }}>
            {helperText}
          </FormHelperText>
        )}
      </div>
    </LocalizationProvider>
  );
};
